import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, HostListener } from '@angular/core';
import { SpreadingState } from "@components/main/spreading/state/manager";
import { AutocompleteOption, Categorization, Row } from "@components/main/spreading/models/models";
import { CategorizationViewConfig, CategorizationViewKey, CATEGORIZATION_VIEW_CONFIGS, DEFAULT_CATEGORIZATION_VIEW, DocumentItemCategorizePayload, getAvailableViewConfigs, getViewLabel } from './categorization-view.types';

const VIEWPORT_EDGE_PADDING = 10;

@Component({
  selector: 'app-add-categorization-modal',
  templateUrl: './add-categorization-modal.component.html',
  styleUrls: ['./add-categorization-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AddCategorizationModalComponent implements OnInit, AfterViewInit {
  @Input() state: SpreadingState;
  @Input() row: Row;
  @Input() viewInNativeLanguage = true;
  @Input() buttonElement: HTMLElement;
  @Input() set initialView(view: CategorizationViewKey) {
    if (view) {
      this.selectedView = view;
    }
  }
  @Output() stateChanged = new EventEmitter<SpreadingState>();
  @Output() closeModal = new EventEmitter<boolean>();
  @Output() manualSourceRowInserted = new EventEmitter<Row>();
  @Output() offsetAdjustmentInserted = new EventEmitter<number>();

  @ViewChild('modalContainer', { static: false }) modalContainer: ElementRef;

  navigationOptions: CategorizationViewConfig[] = CATEGORIZATION_VIEW_CONFIGS;
  selectedView: CategorizationViewKey = DEFAULT_CATEGORIZATION_VIEW;

  get selectedViewTitle(): string {
    return getViewLabel(this.selectedView);
  }

  get isRollupTemplateItem(): boolean {
    const lineItemId = this.row?.lineItemId;
    const rollups = this.state?.rollupOptions;
    if (lineItemId == null || !rollups?.length) {
      return false;
    }
    return rollups.some((opt) => opt.id === lineItemId);
  }

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.navigationOptions = getAvailableViewConfigs(this.isRollupTemplateItem);
    if (!this.navigationOptions.some((option) => option.value === this.selectedView)) {
      this.selectedView = DEFAULT_CATEGORIZATION_VIEW;
    }
  }

  selectView(view: CategorizationViewKey): void {
    this.selectedView = view;
    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.buttonElement && this.modalContainer) {
        this.positionModal();
      }
      this.focusFirstElement();
    }, 0);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.onClose(false);
      return;
    }
    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  private focusableElements(): HTMLElement[] {
    const root = this.modalContainer?.nativeElement as HTMLElement | undefined;
    if (!root) {
      return [];
    }
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(el => el.offsetParent !== null);
  }

  private focusFirstElement(): void {
    this.focusableElements()[0]?.focus();
  }

  private trapFocus(event: KeyboardEvent): void {
    const focusable = this.focusableElements();
    if (!focusable.length) {
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private positionModal(): void {
    if (!this.buttonElement || !this.modalContainer) {
      return;
    }

    const buttonRect = this.buttonElement.getBoundingClientRect();
    const modalElement = this.modalContainer.nativeElement;
    const modalContent = modalElement.querySelector('.modal-content');
    
    if (!modalContent) {
      return;
    }

    // Position below and to the right of the button
    const top = buttonRect.bottom + 5; // 5px gap below
    const left = buttonRect.right + 5; // 5px gap to the right

    // Ensure modal doesn't go off screen
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const modalWidth = (modalContent as HTMLElement).offsetWidth || 300;
    const modalHeight = (modalContent as HTMLElement).offsetHeight || 200;

    let finalLeft = left;
    let finalTop = top;

    // Adjust if modal would go off right edge
    if (left + modalWidth > viewportWidth) {
      finalLeft = viewportWidth - modalWidth - VIEWPORT_EDGE_PADDING;
    }

    // Adjust if modal would go off bottom edge
    if (top + modalHeight > viewportHeight) {
      // Position above instead, pushed back down if it would clear the top of the viewport
      finalTop = Math.max(buttonRect.top - modalHeight - 5, VIEWPORT_EDGE_PADDING);
    }

    modalElement.style.position = 'fixed';
    modalElement.style.top = `${finalTop}px`;
    modalElement.style.left = `${finalLeft}px`;
    modalElement.style.zIndex = '1050';
  }

  onClose(expandChildren: boolean = false): void {
    this.buttonElement?.focus();
    this.closeModal.emit(expandChildren);
  }

  onInsertManualItem(): void {
    if (!this.state || this.row?.lineItemId == null) {
      return;
    }
    const templateLabel = this.row.userFacingLabel?.trim()
      ? this.row.userFacingLabel
      : this.row.label || '';
    const isValidation = this.isRollupTemplateItem;
    const newSourceRow = this.state.insertManualCategorizedChild(
      this.row.lineItemId,
      templateLabel,
      isValidation
    );
    this.stateChanged.emit(this.state);
    this.manualSourceRowInserted.emit(newSourceRow);
    this.onClose(true);
  }

  onInsertOffsetAdjustment(offsetTarget: AutocompleteOption): void {
    if (!this.state || this.row?.lineItemId == null || offsetTarget?.id == null) {
      return;
    }
    const primaryRow = this.state.insertOffsetAdjustmentPair(
      this.row.lineItemId,
      offsetTarget.id,
      offsetTarget.value ?? ''
    );
    this.stateChanged.emit(this.state);
    this.manualSourceRowInserted.emit(primaryRow);
    this.offsetAdjustmentInserted.emit(offsetTarget.id);
    this.onClose(true);
  }

  onDocumentItemCategorize(payload: DocumentItemCategorizePayload): void {
    const selectedSourceRows = payload.rows;
    if (!this.state?.sourceRows || !this.row || selectedSourceRows.length === 0) {
      return;
    }
    const templateLineItemId = this.row.lineItemId;
    if (templateLineItemId == null) {
      return;
    }
    const templateLineItemLabel = this.row.userFacingLabel || this.row.label || '';
    const categorizations: Categorization[] = selectedSourceRows.map((sourceRow) => ({
      sourceRow,
      templateLineItemId,
      templateLineItemLabel,
      isValidation: payload.isValidation,
    }));
    this.state.categorizeRows(categorizations);
    this.stateChanged.emit(this.state);
    this.onClose(true);
  }
}
